import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContractOrigin } from '../../utils/contractOrigin';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardHeader,
    Avatar,
    Chip,
    Button,
    Stack,
    IconButton,
    Divider,
    Paper,
    Skeleton,
    alpha,
    Tooltip,
    Breadcrumbs,
    Link as MuiLink,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    Collapse,
    ToggleButton,
    ToggleButtonGroup,
    Alert,
    useTheme
} from '@mui/material';
import {
    ArrowBack as BackIcon,
    ChevronRight as ChevronRightIcon,
    ChevronLeft as ChevronLeftIcon,
    Business as BusinessIcon,
    Description as DescriptionIcon,
    Sensors as SensorsIcon,
    Today as TodayIcon,
    DateRange as DateRangeIcon,
    CalendarMonth as CalendarMonthIcon,
    PrecisionManufacturing as MachineIcon,
    TimerOutlined as TimerIcon,
    MoreTimeOutlined as ExtraIcon,
    SpeedOutlined as EngineIcon,
    KeyboardArrowDown as ArrowDownIcon,
    KeyboardArrowUp as ArrowUpIcon,
    NotificationsActive as AlertIcon,
    FiberManualRecord as DotIcon,
    ArticleOutlined as ReportIcon
} from '@mui/icons-material';

import mewpService from '../../services/mewpService';
import geofenceService from '../../services/geofenceService';
import { formatToIST } from '../../utils/dataUtils';
import { machineLabel } from '../../utils/machineLabel';

// ─── Date helpers (IST calendar dates, YYYY-MM-DD) ────────────────────────────
// All period math reads only a Date's Y/M/D fields. We seed the anchor with a
// local-midnight Date whose Y/M/D equal the IST calendar date, so the from/to we
// send (and the "latest period" gate) always match the backend, which buckets
// telemetry by IST regardless of the browser's timezone.
const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dayOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const istToday = () => {
    const s = new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' }); // YYYY-MM-DD (IST)
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d);
};

// from/to (inclusive) for a period anchored on a given day
function rangeFor(period, anchor) {
    const d = dayOnly(anchor);
    if (period === 'day') return { from: toYmd(d), to: toYmd(d) };
    if (period === 'week') {
        const dow = (d.getDay() + 6) % 7; // Monday = 0
        const mon = new Date(d); mon.setDate(d.getDate() - dow);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        return { from: toYmd(mon), to: toYmd(sun) };
    }
    const first = new Date(d.getFullYear(), d.getMonth(), 1);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0);
    return { from: toYmd(first), to: toYmd(last) };
}

function shiftAnchor(period, anchor, delta) {
    const d = dayOnly(anchor);
    if (period === 'day') d.setDate(d.getDate() + delta);
    else if (period === 'week') d.setDate(d.getDate() + delta * 7);
    else d.setMonth(d.getMonth() + delta);
    return d;
}

function periodLabel(period, range) {
    const fmt = (ymd, opts) => new Date(`${ymd}T00:00:00`).toLocaleDateString('en-IN', opts);
    if (period === 'day') return fmt(range.from, { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
    if (period === 'week') {
        const sameYear = range.from.slice(0, 4) === range.to.slice(0, 4);
        return `${fmt(range.from, { day: '2-digit', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) })} – ${fmt(range.to, { day: '2-digit', month: 'short', year: 'numeric' })}`;
    }
    return fmt(range.from, { month: 'long', year: 'numeric' });
}

const fmtHours = (n) => `${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })}h`;

// Full API base (for window.open of the server-rendered report — not an axios call)
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';
const openDeviceReport = (equipmentId, from, to) =>
    window.open(`${API_BASE}/downloads/device-report/${encodeURIComponent(equipmentId)}?from=${from}&to=${to}`, '_blank', 'noopener');

// Alarm severity → semantic colour (per DESIGN_GUIDE §3). The alarms table uses
// severity ENUM('info','warning','critical'); fall back on open/closed status when
// severity is absent.
const alarmColor = (a) => {
    const sev = (a.severity || '').toLowerCase();
    if (sev === 'critical' || sev === 'high') return '#ef4444';
    if (sev === 'warning' || sev === 'medium') return '#f59e0b';
    if (sev === 'info') return '#0ea5e9';
    return a.alarm_status === 'OPEN' ? '#ef4444' : '#10b981';
};

const ContractSummaryPage = () => {
    const { contractId } = useParams();
    const navigate = useNavigate();
    const origin = getContractOrigin();   // the door this contract was opened from
    const theme = useTheme();

    const [contract, setContract] = useState(null);
    const [period, setPeriod] = useState('day');
    const [anchor, setAnchor] = useState(() => istToday());

    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [alarms, setAlarms] = useState([]);
    const [alarmsLoading, setAlarmsLoading] = useState(true);

    const range = useMemo(() => rangeFor(period, anchor), [period, anchor]);
    const todayYmd = useMemo(() => toYmd(istToday()), []);
    const atLatest = range.to >= todayYmd; // can't navigate into the future

    // Contract identity — loaded once.
    useEffect(() => {
        let active = true;
        mewpService.getContractById(contractId)
            .then((res) => { if (active && res?.success) setContract(res.data); })
            .catch(() => { /* identity is non-critical */ });
        return () => { active = false; };
    }, [contractId]);

    // Summary + alarms — reload whenever the range changes.
    useEffect(() => {
        let active = true;
        setLoading(true); setError('');
        mewpService.getContractSummary(contractId, range.from, range.to)
            .then((res) => {
                if (!active) return;
                if (res?.success) setSummary(res);
                else { setSummary(null); setError(res?.message || 'Failed to load the summary.'); }
            })
            .catch(() => { if (active) { setSummary(null); setError('Failed to load the summary.'); } })
            .finally(() => { if (active) setLoading(false); });

        setAlarmsLoading(true);
        geofenceService.listAlarms({ contract_id: contractId, from: range.from, to: range.to, limit: 1000 })
            .then((res) => { if (active) setAlarms(Array.isArray(res?.data) ? res.data : []); })
            .catch(() => { if (active) setAlarms([]); })
            .finally(() => { if (active) setAlarmsLoading(false); });

        return () => { active = false; };
    }, [contractId, range.from, range.to]);

    // Alerts per machine (for the per-machine row chip).
    const alertsByMachine = useMemo(() => {
        const map = {};
        for (const a of alarms) {
            const k = a.equipment_id || '—';
            map[k] = (map[k] || 0) + 1;
        }
        return map;
    }, [alarms]);

    const machines = summary?.machines || [];

    const tiles = [
        {
            key: 'machines', label: 'Machines Worked', icon: <MachineIcon />, color: theme.palette.primary.main,
            value: Number(summary?.machines_worked || 0).toLocaleString('en-IN'),
            sub: `of ${Number(summary?.machine_count || 0).toLocaleString('en-IN')} on site`
        },
        {
            key: 'running', label: 'Running Hours', icon: <TimerIcon />, color: theme.palette.info.main,
            value: fmtHours(summary?.total_working_hours), sub: `${Number(summary?.working_days || 0)} working day${summary?.working_days === 1 ? '' : 's'}`
        },
        {
            key: 'extra', label: 'Extra Hours', icon: <ExtraIcon />, color: theme.palette.warning.main,
            value: fmtHours(summary?.total_extra_hours), sub: 'outside shift hours'
        },
        {
            key: 'engine', label: 'Engine Hours', icon: <EngineIcon />, color: theme.palette.success.main,
            value: fmtHours(summary?.total_engine_hours), sub: 'metered run time'
        }
    ];

    return (
        <Box p={3} sx={{ bgcolor: '#f8fafc', minHeight: '100vh' }} className="animate-fade-in">
            {/* Breadcrumb */}
            <Box display="flex" alignItems="center" gap={2} mb={3}>
                <IconButton onClick={() => navigate(`/contract-live/${contractId}`)} sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }} aria-label="Back to live view">
                    <BackIcon />
                </IconButton>
                <Breadcrumbs separator={<ChevronRightIcon fontSize="small" />}>
                    <MuiLink component="button" onClick={() => navigate(origin.path)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>{origin.label}</MuiLink>
                    <MuiLink component="button" onClick={() => navigate(`/contract-live/${contractId}`)} underline="hover" color="inherit" sx={{ fontWeight: 600 }}>{contractId}</MuiLink>
                    <Typography color="text.primary" sx={{ fontWeight: 700 }}>Summary</Typography>
                </Breadcrumbs>
            </Box>

            {/* Top Bar: identity + period selector + actions */}
            <Card sx={{ mb: 3, borderRadius: '24px' }}>
                <CardContent sx={{ p: '20px !important' }}>
                    <Box display="flex" alignItems="center" sx={{ gap: 3, flexWrap: { xs: 'wrap', xl: 'nowrap' } }}>
                        {/* Identity */}
                        <Box display="flex" alignItems="center" gap={2} sx={{ minWidth: 260 }}>
                            <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', borderRadius: '12px', width: 52, height: 52 }} variant="rounded">
                                <BusinessIcon />
                            </Avatar>
                            <Box>
                                <Typography variant="h6" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{contractId}</Typography>
                                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={0.5}>
                                    {contract?.company_name || contract?.customer || 'Rental Contract'}
                                </Typography>
                                <Chip label="Usage Summary" color="primary" size="small"
                                    sx={{ fontWeight: 800, borderRadius: '6px', height: '22px', fontSize: '11px' }} />
                            </Box>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ opacity: 0.6, display: { xs: 'none', xl: 'block' } }} />

                        {/* Period selector + date nav */}
                        <Box flexGrow={1} display="flex" flexDirection="column" alignItems={{ xs: 'flex-start', xl: 'center' }} gap={1.25}>
                            <ToggleButtonGroup
                                value={period}
                                exclusive
                                size="small"
                                onChange={(_e, v) => { if (v) { setPeriod(v); setAnchor(istToday()); } }}
                                sx={{
                                    bgcolor: '#f1f5f9', borderRadius: '12px', p: '4px',
                                    '& .MuiToggleButton-root': {
                                        border: 0, borderRadius: '8px !important', textTransform: 'none',
                                        fontWeight: 700, px: 2, color: 'text.secondary', gap: 0.75
                                    },
                                    '& .Mui-selected': { bgcolor: 'white !important', color: 'primary.main !important', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }
                                }}
                            >
                                <ToggleButton value="day"><TodayIcon sx={{ fontSize: 18 }} /> Day</ToggleButton>
                                <ToggleButton value="week"><DateRangeIcon sx={{ fontSize: 18 }} /> Week</ToggleButton>
                                <ToggleButton value="month"><CalendarMonthIcon sx={{ fontSize: 18 }} /> Month</ToggleButton>
                            </ToggleButtonGroup>

                            <Stack direction="row" alignItems="center" spacing={1}>
                                <IconButton size="small" onClick={() => setAnchor((a) => shiftAnchor(period, a, -1))} aria-label="Previous period"
                                    sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                                    <ChevronLeftIcon fontSize="small" />
                                </IconButton>
                                <Typography variant="subtitle2" fontWeight={800} sx={{ minWidth: 168, textAlign: 'center' }}>
                                    {periodLabel(period, range)}
                                </Typography>
                                <Tooltip title={atLatest ? 'Already at the latest period' : ''}>
                                    <span>
                                        <IconButton size="small" disabled={atLatest} onClick={() => setAnchor((a) => shiftAnchor(period, a, 1))} aria-label="Next period"
                                            sx={{ bgcolor: 'white', border: '1px solid', borderColor: 'divider' }}>
                                            <ChevronRightIcon fontSize="small" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Stack>
                        </Box>

                        <Divider orientation="vertical" flexItem sx={{ opacity: 0.6, display: { xs: 'none', xl: 'block' } }} />

                        {/* Actions: Data (back to live) + View Contract */}
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <Button variant="outlined" size="small" startIcon={<SensorsIcon sx={{ fontSize: 18 }} />}
                                onClick={() => navigate(`/contract-live/${contractId}`)}
                                sx={{ borderRadius: '10px', fontWeight: 700, textTransform: 'none', whiteSpace: 'nowrap' }}>
                                Data
                            </Button>
                            <Button variant="contained" size="small" startIcon={<DescriptionIcon sx={{ fontSize: 18 }} />}
                                onClick={() => navigate(`/contract/${contractId}`)}
                                sx={{ borderRadius: '10px', fontWeight: 700, px: 2, textTransform: 'none', whiteSpace: 'nowrap', '&:hover': { bgcolor: 'primary.dark' } }}>
                                View Contract
                            </Button>
                        </Stack>
                    </Box>
                </CardContent>
            </Card>

            {/* KPI banner */}
            <Card sx={{ mb: 3, borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08)' }}>
                <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {tiles.map((t) => (
                            <Grid size={{ xs: 6, md: 3 }} key={t.key}>
                                <Box display="flex" alignItems="center" gap={2}>
                                    <Avatar variant="rounded" sx={{ bgcolor: alpha(t.color, 0.12), color: t.color, width: 52, height: 52, borderRadius: '14px' }}>
                                        {t.icon}
                                    </Avatar>
                                    <Box minWidth={0}>
                                        <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.3, display: 'block' }}>
                                            {t.label}
                                        </Typography>
                                        {loading ? (
                                            <Skeleton width={70} height={34} />
                                        ) : error ? (
                                            <Typography variant="h5" fontWeight={800} color="text.disabled" sx={{ lineHeight: 1.1 }}>—</Typography>
                                        ) : (
                                            <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1.1 }}>{t.value}</Typography>
                                        )}
                                        <Typography variant="caption" color="text.secondary" noWrap>{t.sub}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Per-machine breakdown */}
                <Grid size={{ xs: 12, lg: 8 }}>
                    <Card sx={{ borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08)', height: '100%' }}>
                        <CardHeader
                            avatar={<Avatar variant="rounded" sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', width: 36, height: 36, borderRadius: '12px' }}><MachineIcon sx={{ fontSize: 18 }} /></Avatar>}
                            title={<Typography variant="subtitle1" fontWeight={800}>Machine Breakdown</Typography>}
                            subheader={<Typography variant="caption" color="text.secondary">Per-machine usage for {periodLabel(period, range)}. Click a row for the daily detail.</Typography>}
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box sx={{ p: 0 }}>
                            {loading ? (
                                <Box p={2}>
                                    {[0, 1, 2, 3].map((i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 2 }} />)}
                                </Box>
                            ) : error ? (
                                <Box p={2}><Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert></Box>
                            ) : machines.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} color="text.secondary">
                                    <MachineIcon sx={{ fontSize: 48, opacity: 0.2, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600}>No machines were on this contract in this period.</Typography>
                                </Box>
                            ) : (
                                <TableContainer sx={{ maxHeight: '62vh' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ width: 40 }} />
                                                <TableCell sx={{ fontWeight: 700 }}>Machine</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Days</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Running</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Engine</TableCell>
                                                <TableCell align="right" sx={{ fontWeight: 700 }}>Extra</TableCell>
                                                <TableCell align="center" sx={{ fontWeight: 700 }}>Alerts</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {machines.map((m) => (
                                                <MachineRow key={m.equipment_id} m={m} alerts={alertsByMachine[m.equipment_id] || 0} from={range.from} to={range.to} />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </Box>
                    </Card>
                </Grid>

                {/* Alerts in this period */}
                <Grid size={{ xs: 12, lg: 4 }}>
                    <Card sx={{ borderRadius: '24px', boxShadow: '0 10px 25px -5px rgba(0,0,0,0.08)', height: '100%' }}>
                        <CardHeader
                            avatar={<Avatar variant="rounded" sx={{ bgcolor: alpha(theme.palette.error.main, 0.1), color: 'error.main', width: 36, height: 36, borderRadius: '12px' }}><AlertIcon sx={{ fontSize: 18 }} /></Avatar>}
                            title={
                                <Box display="flex" alignItems="center" gap={1}>
                                    <Typography variant="subtitle1" fontWeight={800}>Alerts in this Period</Typography>
                                    {alarms.length > 0 && (
                                        <Chip label={alarms.length} size="small" sx={{ bgcolor: 'error.main', color: 'white', fontWeight: 800, height: 22, fontSize: '11px' }} />
                                    )}
                                </Box>
                            }
                            subheader={<Typography variant="caption" color="text.secondary">Geofence & machine alarms opened in {periodLabel(period, range)}.</Typography>}
                            sx={{ borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}
                        />
                        <Box p={2} sx={{ maxHeight: '62vh', overflowY: 'auto' }}>
                            {alarmsLoading ? (
                                <Stack spacing={1}>{[0, 1, 2].map((i) => <Skeleton key={i} variant="rectangular" height={64} sx={{ borderRadius: 2 }} />)}</Stack>
                            ) : alarms.length === 0 ? (
                                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={5} color="text.secondary">
                                    <AlertIcon sx={{ fontSize: 40, opacity: 0.25, mb: 1 }} />
                                    <Typography variant="body2" fontWeight={600}>No alerts in this period.</Typography>
                                </Box>
                            ) : (
                                <Stack spacing={1.2}>
                                    {alarms.map((a) => {
                                        const c = alarmColor(a);
                                        const isOpen = a.alarm_status === 'OPEN';
                                        return (
                                            <Paper key={a.id} elevation={0} sx={{
                                                p: 1.5, borderRadius: '12px', border: '1px solid',
                                                borderLeft: `4px solid ${c}`, borderColor: 'divider', bgcolor: alpha(c, 0.05)
                                            }}>
                                                <Box display="flex" alignItems="center" gap={1} mb={0.3} flexWrap="wrap">
                                                    <Chip label={a.alarm_name} size="small" sx={{ fontWeight: 800, height: 20, fontSize: '10px', bgcolor: c, color: 'white' }} />
                                                    {a.category && <Chip label={a.category} size="small" variant="outlined" sx={{ fontWeight: 700, height: 20, fontSize: '10px' }} />}
                                                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'primary.main' }}>{machineLabel(a)}</Typography>
                                                </Box>
                                                {a.message && (
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{a.message}</Typography>
                                                )}
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.3 }}>
                                                    <DotIcon sx={{ fontSize: 9, color: c }} />
                                                    {isOpen ? 'Open' : 'Closed'} · {a.created_at ? formatToIST(a.created_at, 'dd MMM, hh:mm a') : '-'}
                                                </Typography>
                                            </Paper>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

// One machine row + collapsible daily detail.
const MachineRow = ({ m, alerts, from, to }) => {
    const [open, setOpen] = useState(false);
    const hasDays = (m.days || []).length > 0;
    return (
        <>
            <TableRow hover sx={{ cursor: hasDays ? 'pointer' : 'default', '& > *': { borderBottom: open ? 'unset' : undefined } }}
                onClick={() => hasDays && setOpen((o) => !o)}>
                <TableCell sx={{ width: 40 }}>
                    {hasDays && (
                        <IconButton size="small" aria-label={open ? 'Collapse' : 'Expand'}>
                            {open ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                        </IconButton>
                    )}
                </TableCell>
                <TableCell>
                    <Typography variant="body2" fontWeight={700}>{m.serial_number}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                        {m.equipment_id}{m.model ? ` · ${m.model}` : ''}
                    </Typography>
                    <Button
                        size="small" variant="text" startIcon={<ReportIcon sx={{ fontSize: 15 }} />}
                        onClick={(e) => { e.stopPropagation(); openDeviceReport(m.equipment_id, from, to); }}
                        sx={{ mt: 0.25, ml: -0.5, px: 0.75, py: 0, minWidth: 0, fontSize: 11, fontWeight: 700, textTransform: 'none', color: 'primary.main' }}
                    >
                        Report
                    </Button>
                </TableCell>
                <TableCell align="right">{m.working_days}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtHours(m.working_hours)}</TableCell>
                <TableCell align="right">{fmtHours(m.engine_hours)}</TableCell>
                <TableCell align="right">
                    {m.extra_hours > 0
                        ? <Chip size="small" label={fmtHours(m.extra_hours)} color="warning" variant="filled" />
                        : <Typography variant="body2" color="text.disabled">0h</Typography>}
                </TableCell>
                <TableCell align="center">
                    {alerts > 0
                        ? <Chip size="small" label={alerts} color="error" sx={{ fontWeight: 800, height: 22 }} />
                        : <Typography variant="caption" color="text.disabled">—</Typography>}
                </TableCell>
            </TableRow>
            {hasDays && (
                <TableRow>
                    <TableCell colSpan={7} sx={{ py: 0, bgcolor: '#fafbfc', borderBottom: open ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            <Box sx={{ py: 1.5, px: 2 }}>
                                <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ textTransform: 'uppercase', letterSpacing: 0.3 }}>
                                    Daily detail
                                </Typography>
                                <Table size="small" sx={{ mt: 0.5 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 700, border: 0 }}>Date</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, border: 0 }}>Running</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, border: 0 }}>Engine</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 700, border: 0 }}>Extra</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {m.days.map((d) => (
                                            <TableRow key={d.date}>
                                                <TableCell sx={{ border: 0 }}>
                                                    {new Date(`${d.date}T00:00:00`).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short' })}
                                                </TableCell>
                                                <TableCell align="right" sx={{ border: 0 }}>{fmtHours(d.working_hours)}</TableCell>
                                                <TableCell align="right" sx={{ border: 0 }}>{fmtHours(d.engine_hours)}</TableCell>
                                                <TableCell align="right" sx={{ border: 0, color: d.extra_hours > 0 ? 'warning.dark' : 'text.secondary', fontWeight: d.extra_hours > 0 ? 700 : 400 }}>
                                                    {fmtHours(d.extra_hours)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Collapse>
                    </TableCell>
                </TableRow>
            )}
        </>
    );
};

export default ContractSummaryPage;
